import React from 'react';
import { FiGift, FiPercent, FiCreditCard, FiCalendar, FiUser } from 'react-icons/fi';
import QRCode from 'react-qr-code';
import { fmtParede } from '../utils/tempoCv';

interface VoucherCardProps {
  titulo: string;
  valor?: number | null;
  percentagem_desconto?: number | null;
  tipo_categoria: 'valor' | 'desconto';
  cor_fundo?: string;
  cor_texto?: string;
  empresa_nome: string;
  empresa_logo?: string | null;
  beneficiario_nome?: string;
  data_validade?: string | null;
  qr_code_data?: string;
  codigo_unico?: string;
  status?: string;
  compact?: boolean;
  onClick?: () => void;
}

const VoucherCard: React.FC<VoucherCardProps> = ({
  titulo,
  valor,
  percentagem_desconto,
  tipo_categoria,
  cor_fundo = '#1976d2',
  cor_texto = '#ffffff',
  empresa_nome,
  empresa_logo,
  beneficiario_nome,
  data_validade,
  qr_code_data,
  codigo_unico,
  status,
  compact = false,
  onClick,
}) => {
  const formatValor = () => {
    if (tipo_categoria === 'desconto' && percentagem_desconto) {
      return `${percentagem_desconto}% OFF`;
    }
    if (valor) {
      return `${valor.toLocaleString('pt-CV')} ECV`;
    }
    return '-';
  };

  const getStatusBadge = () => {
    if (!status) return null;

    const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
      ativo: { bg: 'bg-green-500', text: 'text-white', label: 'Ativo' },
      parcialmente_usado: { bg: 'bg-yellow-500', text: 'text-white', label: 'Parcial' },
      usado: { bg: 'bg-gray-500', text: 'text-white', label: 'Usado' },
      expirado: { bg: 'bg-red-500', text: 'text-white', label: 'Expirado' },
      cancelado: { bg: 'bg-red-600', text: 'text-white', label: 'Cancelado' },
    };

    const config = statusConfig[status] || statusConfig.ativo;

    return (
      <span className={`absolute top-2 right-2 px-2 py-0.5 text-xs rounded-full ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (compact) {
    return (
      <div
        onClick={onClick}
        className="relative rounded-xl p-4 cursor-pointer transition-transform hover:scale-[1.02] shadow-lg"
        style={{ backgroundColor: cor_fundo, color: cor_texto }}
      >
        {getStatusBadge()}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            {tipo_categoria === 'valor' ? <FiCreditCard size={20} /> : <FiPercent size={20} />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold truncate">{titulo}</p>
            <p className="text-sm opacity-80">{empresa_nome}</p>
          </div>
          <div className="text-right">
            <p className="font-bold text-lg">{formatValor()}</p>
          </div>
        </div>
      </div>
    );
  }

  const isActive = !status || status === 'ativo' || status === 'parcialmente_usado';
  const isUsed = status === 'usado' || status === 'expirado' || status === 'cancelado';

  // Gradient per type/status
  const gradient = isUsed
    ? 'linear-gradient(135deg, #9CA3AF, #6B7280)'
    : tipo_categoria === 'desconto'
    ? 'linear-gradient(135deg, #6A11CB, #2575FC)'
    : `linear-gradient(135deg, ${cor_fundo || '#7340BE'}, ${cor_fundo ? cor_fundo + 'CC' : '#5325A4'})`;

  return (
    <div
      onClick={onClick}
      className="relative rounded-2xl overflow-hidden cursor-pointer transition-all hover:shadow-2xl max-w-md"
      style={{
        background: gradient,
        color: cor_texto || '#ffffff',
        boxShadow: isUsed
          ? '0 10px 30px rgba(107,114,128,0.3)'
          : `0 10px 30px ${cor_fundo || '#7340BE'}40`,
      }}
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none">
        <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-white transform translate-x-10 -translate-y-10" />
        <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-white transform -translate-x-10 translate-y-10" />
      </div>

      {/* ── Top section: Info ── */}
      <div className="relative p-5 pb-3">
        {/* Header row */}
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {empresa_logo ? (
              <img src={empresa_logo} alt={empresa_nome}
                className="w-10 h-10 rounded-xl object-cover bg-white/20 flex-shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <FiGift size={18} />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-xs opacity-70 truncate">{empresa_nome}</p>
              <p className="font-semibold text-base leading-tight truncate">{titulo}</p>
            </div>
          </div>
          {getStatusBadge()}
        </div>

        {/* Value display */}
        <div className="flex items-baseline gap-2 mt-2">
          {tipo_categoria === 'valor' ? (
            <>
              <span className="text-4xl font-extrabold tracking-tight">
                {(valor || 0).toLocaleString('pt-CV')}
              </span>
              <span className="text-lg opacity-70 font-medium">ECV</span>
            </>
          ) : (
            <>
              <span className="text-4xl font-extrabold tracking-tight">
                {percentagem_desconto || 0}%
              </span>
              <span className="text-lg opacity-70 font-medium">OFF</span>
            </>
          )}
        </div>
      </div>

      {/* ── Dashed divider with cut-out circles ── */}
      <div className="relative h-5 flex items-center">
        {/* Dashed line */}
        <div className="flex-1 mx-6 border-t border-dashed border-white/20" />
        {/* Left circle cut-out */}
        <div className="absolute -left-2.5 w-5 h-5 rounded-full"
          style={{ backgroundColor: '#f9fafb' }} />
        {/* Right circle cut-out */}
        <div className="absolute -right-2.5 w-5 h-5 rounded-full"
          style={{ backgroundColor: '#f9fafb' }} />
      </div>

      {/* ── Bottom section: QR + details ── */}
      <div className="relative px-5 pb-5 pt-1 flex gap-4">
        {/* QR Code */}
        {isActive && qr_code_data ? (
          <div className="bg-white p-2 rounded-xl flex-shrink-0 shadow-inner">
            <QRCode value={qr_code_data} size={90} />
          </div>
        ) : (
          <div className="w-[106px] h-[106px] rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
            {isUsed ? (
              <FiCreditCard size={36} className="opacity-30" />
            ) : (
              <div className="text-center opacity-40">
                <FiCreditCard size={28} />
                <p className="text-[9px] mt-1">QR</p>
              </div>
            )}
          </div>
        )}

        {/* Details */}
        <div className="flex flex-col justify-between flex-1 min-w-0 py-0.5">
          {codigo_unico && (
            <p className="font-mono text-xs opacity-60 tracking-wider truncate">
              {codigo_unico}
            </p>
          )}
          <div className="space-y-1.5 mt-2">
            {beneficiario_nome && (
              <div className="flex items-center gap-1.5 text-xs opacity-75">
                <FiUser size={12} className="flex-shrink-0" />
                <span className="truncate">{beneficiario_nome}</span>
              </div>
            )}
            {data_validade ? (
              <div className="flex items-center gap-1.5 text-xs opacity-75">
                <FiCalendar size={12} className="flex-shrink-0" />
                <span>Válido até {fmtParede(data_validade, { day: '2-digit', month: '2-digit', year: 'numeric' }, 'pt-PT')}</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 text-xs opacity-75">
                <FiCalendar size={12} className="flex-shrink-0" />
                <span>Sem validade</span>
              </div>
            )}
          </div>
          {isActive && (
            <p className="text-[10px] opacity-40 mt-2 italic">
              Apresente o QR ao comerciante para validar
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoucherCard;
